/**
 * Single owner for per-renderer gapless (SetNext) preferences.
 * Supabase user_preferences.renderer_gapless_prefs = source of truth;
 * localStorage = cache only; BATON syncGaplessPref = real-time propagation.
 *
 * No module-scope browser API access and no static imports of transport/sync
 * modules — keeps Node-environment test imports safe.
 */

import { useMaestroStore } from '../store/maestroStore';

export const DEFAULT_RENDERER_GAPLESS = true;
const LS_KEY = 'maestro_renderer_gapless_prefs_cache';

export type GaplessPrefEntry = {
  enabled: boolean;
  updatedAt: number;
};

export type GaplessPrefsMap = Record<string, GaplessPrefEntry>;

type GaplessPrefsCache = {
  prefs: GaplessPrefsMap;
};

let prefs: GaplessPrefsMap = {};
const listeners = new Set<() => void>();
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let hydratedFromServer = false;

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function getToken(): string | null {
  if (!hasLocalStorage()) return null;
  try {
    const raw = localStorage.getItem('maestro_auth');
    if (raw) return JSON.parse(raw)?.accessToken || null;
  } catch { /* ignore */ }
  return null;
}

function readLocalCache(): GaplessPrefsMap {
  if (!hasLocalStorage()) return {};
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as GaplessPrefsCache;
    return parsed?.prefs && typeof parsed.prefs === 'object' ? parsed.prefs : {};
  } catch {
    return {};
  }
}

function writeLocalCache(): void {
  if (!hasLocalStorage()) return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ prefs }));
  } catch { /* ignore */ }
}

function notifyListeners(): void {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

function logGaplessWrite(value: boolean, source: string, rendererId: string, updatedAt: number): void {
  console.log('[settings] gapless_write', JSON.stringify({ value, source, rendererId, updatedAt }));
}

export function subscribeGaplessPrefs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getGaplessPrefEntry(rendererId: string | null | undefined): GaplessPrefEntry | undefined {
  if (!rendererId) return undefined;
  return prefs[rendererId];
}

export function hasRendererGaplessPref(rendererId: string | null | undefined): boolean {
  if (!rendererId) return false;
  return prefs[rendererId] !== undefined;
}

/** Resolved preference: explicit user value or default when none persisted. */
export function getRendererGaplessEnabled(rendererId: string | null | undefined): boolean {
  if (!rendererId) return DEFAULT_RENDERER_GAPLESS;
  const entry = prefs[rendererId];
  if (entry !== undefined) return entry.enabled;
  return DEFAULT_RENDERER_GAPLESS;
}

function mergePrefEntry(rendererId: string, enabled: boolean, updatedAt: number): boolean {
  const existing = prefs[rendererId];
  if (existing && existing.updatedAt > updatedAt) return false;
  if (existing && existing.updatedAt === updatedAt && existing.enabled === enabled) return false;
  prefs[rendererId] = { enabled, updatedAt };
  return true;
}

export function applyInboundGaplessPref(
  rendererId: string,
  enabled: boolean,
  updatedAt: number,
  source: string,
): boolean {
  if (!rendererId) return false;
  const applied = mergePrefEntry(rendererId, enabled, updatedAt);
  if (!applied) return false;
  writeLocalCache();
  logGaplessWrite(enabled, source, rendererId, updatedAt);
  patchZoneGaplessInStore(rendererId, enabled);
  notifyListeners();
  return true;
}

function normalizeRemotePrefs(raw: unknown): GaplessPrefsMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: GaplessPrefsMap = {};
  for (const [rendererId, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!rendererId) continue;
    if (typeof value === 'boolean') {
      out[rendererId] = { enabled: value, updatedAt: 0 };
      continue;
    }
    if (value && typeof value === 'object') {
      const entry = value as { enabled?: unknown; updatedAt?: unknown };
      if (typeof entry.enabled !== 'boolean') continue;
      const updatedAt = typeof entry.updatedAt === 'number' && Number.isFinite(entry.updatedAt)
        ? entry.updatedAt
        : 0;
      out[rendererId] = { enabled: entry.enabled, updatedAt };
    }
  }
  return out;
}

function mergeRemotePrefs(remote: GaplessPrefsMap): void {
  let changed = false;
  for (const [rendererId, entry] of Object.entries(remote)) {
    if (mergePrefEntry(rendererId, entry.enabled, entry.updatedAt)) {
      changed = true;
      patchZoneGaplessInStore(rendererId, entry.enabled);
    }
  }
  if (changed) {
    writeLocalCache();
    notifyListeners();
  }
}

function serializePrefsForServer(): GaplessPrefsMap {
  return { ...prefs };
}

function schedulePersistToServer(): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    void persistGaplessPrefsToServer();
  }, 400);
}

async function persistGaplessPrefsToServer(): Promise<void> {
  const token = getToken();
  if (!token || typeof fetch === 'undefined') return;
  try {
    const { CLOUD_API_URL } = await import('../config/serverConfig');
    await fetch(`${CLOUD_API_URL}/api/user/preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ renderer_gapless_prefs: serializePrefsForServer() }),
    });
  } catch { /* ignore */ }
}

async function pushGaplessToConnector(rendererId: string, enabled: boolean): Promise<void> {
  const { isConnectorAvailable, setRendererGaplessEnabled } = await import('../lib/connector');
  if (!isConnectorAvailable()) return;
  try {
    await setRendererGaplessEnabled(rendererId, enabled);
  } catch (err) {
    const { isLocalLibraryCommandError } = await import('./localLibraryIpcGate');
    if (isLocalLibraryCommandError(err)) {
      throw new Error(
        'Gapless toggle is not allowed on this Connector build — update Maestro or check capabilities/default.json',
      );
    }
    throw err;
  }
}

async function broadcastGaplessPref(rendererId: string, enabled: boolean, updatedAt: number): Promise<void> {
  try {
    const { getSyncHandler } = await import('../handlers/ClientSyncHandler');
    getSyncHandler().broadcastGaplessPref(rendererId, enabled, updatedAt);
  } catch { /* ignore */ }
}

export function patchZoneGaplessInStore(rendererId: string, enabled: boolean): void {
  const store = useMaestroStore.getState();
  const zones = store.discovery?.discoveredZones || [];
  const idx = zones.findIndex((z) => z.id === rendererId);
  if (idx < 0) return;

  const zone = zones[idx];
  const updatedZone = {
    ...zone,
    metadata: {
      ...(zone.metadata || {}),
      gapless_enabled: enabled,
    },
  };
  const nextZones = [...zones];
  nextZones[idx] = updatedZone;
  store.setDiscoveredZones(nextZones);

  const active = store.discovery?.activeZone;
  if (active?.id === rendererId) {
    store.setActiveZone({
      ...active,
      metadata: {
        ...(active.metadata || {}),
        gapless_enabled: enabled,
      },
    } as typeof active);
  }

  const activeRenderer = store.activeConnectorRenderer;
  if (activeRenderer?.id === rendererId) {
    store.setActiveConnectorRenderer({
      ...activeRenderer,
      gapless_enabled: enabled,
    });
  }
}

export function overlayGaplessPrefOnZoneMetadata<T extends { id: string; metadata?: Record<string, unknown> }>(
  zone: T,
): T {
  if (!hasRendererGaplessPref(zone.id)) return zone;
  const enabled = getRendererGaplessEnabled(zone.id);
  return {
    ...zone,
    metadata: {
      ...(zone.metadata || {}),
      gapless_enabled: enabled,
    },
  };
}

export async function writeRendererGaplessEnabled(
  rendererId: string,
  enabled: boolean,
  source: string,
): Promise<void> {
  const previous = prefs[rendererId];
  const updatedAt = Date.now();
  prefs[rendererId] = { enabled, updatedAt };
  writeLocalCache();
  logGaplessWrite(enabled, source, rendererId, updatedAt);
  patchZoneGaplessInStore(rendererId, enabled);
  notifyListeners();
  schedulePersistToServer();
  void broadcastGaplessPref(rendererId, enabled, updatedAt);
  try {
    await pushGaplessToConnector(rendererId, enabled);
  } catch (err) {
    if (previous) {
      prefs[rendererId] = previous;
    } else {
      delete prefs[rendererId];
    }
    writeLocalCache();
    patchZoneGaplessInStore(rendererId, previous?.enabled ?? DEFAULT_RENDERER_GAPLESS);
    notifyListeners();
    throw err;
  }
}

export async function hydrateGaplessPrefsFromServer(): Promise<void> {
  prefs = readLocalCache();
  notifyListeners();

  const token = getToken();
  if (!token || typeof fetch === 'undefined') return;

  try {
    const { CLOUD_API_URL } = await import('../config/serverConfig');
    const res = await fetch(`${CLOUD_API_URL}/api/user/preferences`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const row = await res.json();
    const remote = normalizeRemotePrefs(row?.renderer_gapless_prefs);
    mergeRemotePrefs(remote);
    hydratedFromServer = true;

    for (const [rendererId, entry] of Object.entries(prefs)) {
      if (!entry.enabled) {
        await pushGaplessToConnector(rendererId, false);
      }
    }
  } catch { /* ignore */ }
}

export function resetGaplessPrefsForTests(next: GaplessPrefsMap = {}): void {
  prefs = { ...next };
  hydratedFromServer = false;
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  writeLocalCache();
  notifyListeners();
}

export function isGaplessPrefsHydratedFromServer(): boolean {
  return hydratedFromServer;
}
